import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SaveIcon, ArrowLeftIcon, HelpCircleIcon, CheckIcon, XIcon, ShieldIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
const UserForm: React.FC = () => {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;
  // Mock user data for editing
  const userData = isEditing ? {
    fullName: 'John Smith',
    username: 'johnsmith',
    email: 'john@example.com',
    phone: '+1 (555) 123-4567',
    status: 'active',
    role: 'Admin',
    region: 'North America',
    permissions: {
      dashboard: ['view'],
      merchants: ['view', 'create', 'edit', 'delete'],
      users: ['view', 'create', 'edit', 'delete'],
      transactions: ['view', 'export'],
      reports: ['view', 'export', 'schedule'],
      settings: ['view', 'edit']
    }
  } : {
    fullName: '',
    username: '',
    email: '',
    phone: '',
    status: 'active',
    role: '',
    region: '',
    permissions: {
      dashboard: [],
      merchants: [],
      users: [],
      transactions: [],
      reports: [],
      settings: []
    }
  };
  const [user, setUser] = useState(userData);
  const [formStatus, setFormStatus] = useState<'draft' | 'submitted' | 'error'>('draft');
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setUser(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handlePermissionChange = (module: string, permission: string, checked: boolean) => {
    setUser(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [module]: checked ? [...prev.permissions[module as keyof typeof prev.permissions], permission] : prev.permissions[module as keyof typeof prev.permissions].filter(p => p !== permission)
      }
    }));
  };
  const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const role = e.target.value;
    // Preset permissions based on role
    let newPermissions = {
      ...user.permissions
    };
    if (role === 'Admin') {
      newPermissions = {
        dashboard: ['view'],
        merchants: ['view', 'create', 'edit', 'delete'],
        users: ['view', 'create', 'edit', 'delete'],
        transactions: ['view', 'export'],
        reports: ['view', 'export', 'schedule'],
        settings: ['view', 'edit']
      };
    } else if (role === 'Manager') {
      newPermissions = {
        dashboard: ['view'],
        merchants: ['view', 'create', 'edit'],
        users: ['view'],
        transactions: ['view', 'export'],
        reports: ['view', 'export'],
        settings: ['view']
      };
    } else if (role === 'Agent') {
      newPermissions = {
        dashboard: ['view'],
        merchants: ['view', 'create'],
        users: [],
        transactions: ['view'],
        reports: ['view'],
        settings: []
      };
    } else if (role === 'Auditor') {
      newPermissions = {
        dashboard: ['view'],
        merchants: ['view'],
        users: ['view'],
        transactions: ['view', 'export'],
        reports: ['view', 'export', 'schedule'],
        settings: []
      };
    }
    setUser(prev => ({
      ...prev,
      role,
      permissions: newPermissions
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Submit logic would go here
    setFormStatus('submitted');
    // Navigate back to user list
    setTimeout(() => {
      navigate('/users');
    }, 1500);
  };
  return <div className="space-y-6" data-id="element-6800">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between" data-id="element-6801">
        <div data-id="element-6802">
          <button onClick={() => navigate('/users')} className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300" data-id="element-6803">
            <ArrowLeftIcon className="h-4 w-4 mr-1" data-id="element-6804" />
            Back to Users
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mt-1" data-id="element-6805">
            {isEditing ? 'Edit User' : 'New User'}
          </h1>
        </div>
        <div className="mt-3 sm:mt-0 flex items-center space-x-3" data-id="element-6806">
          {formStatus === 'submitted' && <div className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 px-3 py-1 rounded-md text-sm flex items-center" data-id="element-6807">
              <CheckIcon className="h-4 w-4 mr-1" data-id="element-6808" />
              Submitted Successfully
            </div>}
          {formStatus === 'error' && <div className="bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 px-3 py-1 rounded-md text-sm flex items-center" data-id="element-6809">
              <XIcon className="h-4 w-4 mr-1" data-id="element-6810" />
              Error Submitting Form
            </div>}
          <button type="button" className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" onClick={() => navigate('/users')} data-id="element-6811">
            Cancel
          </button>
          <button type="button" className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" onClick={handleSubmit} data-id="element-6812">
            <SaveIcon className="h-4 w-4 mr-2" data-id="element-6813" />
            {isEditing ? 'Update' : 'Create'} User
          </button>
        </div>
      </div>
      <form onSubmit={handleSubmit} data-id="element-6814">
        <Card title="User Information" data-id="element-6815">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-6816">
            <div data-id="element-6817">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6818">
                Full Name <span className="text-red-500" data-id="element-6819">*</span>
              </label>
              <input type="text" name="fullName" value={user.fullName} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Enter full name" required data-id="element-6820" />
            </div>
            <div data-id="element-6821">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6822">
                Username <span className="text-red-500" data-id="element-6823">*</span>
              </label>
              <input type="text" name="username" value={user.username} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Enter username" required data-id="element-6824" />
            </div>
            <div data-id="element-6825">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6826">
                Email <span className="text-red-500" data-id="element-6827">*</span>
              </label>
              <input type="email" name="email" value={user.email} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Enter email address" required data-id="element-6828" />
            </div>
            <div data-id="element-6829">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6830">
                Phone
              </label>
              <input type="tel" name="phone" value={user.phone} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Enter phone number" data-id="element-6831" />
            </div>
            <div data-id="element-6832">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6833">
                Status <span className="text-red-500" data-id="element-6834">*</span>
              </label>
              <select name="status" value={user.status} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" required data-id="element-6835">
                <option value="active" data-id="element-6836">Active</option>
                <option value="inactive" data-id="element-6837">Inactive</option>
                <option value="locked" data-id="element-6838">Locked</option>
              </select>
            </div>
            <div data-id="element-6839">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6840">
                Region <span className="text-red-500" data-id="element-6841">*</span>
              </label>
              <select name="region" value={user.region} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" required data-id="element-6842">
                <option value="" data-id="element-6843">Select Region</option>
                <option value="North America" data-id="element-6844">North America</option>
                <option value="Europe" data-id="element-6845">Europe</option>
                <option value="Asia" data-id="element-6846">Asia</option>
                <option value="South America" data-id="element-6847">South America</option>
                <option value="Africa" data-id="element-6848">Africa</option>
                <option value="Oceania" data-id="element-6849">Oceania</option>
              </select>
            </div>
          </div>
        </Card>
        <div className="mt-6" data-id="element-6850">
          <Card title="Role & Permissions" data-id="element-6851">
            <div className="space-y-6" data-id="element-6852">
              <div data-id="element-6853">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6854">
                  Role <span className="text-red-500" data-id="element-6855">*</span>
                </label>
                <div className="flex items-center" data-id="element-6856">
                  <select name="role" value={user.role} onChange={handleRoleChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" required data-id="element-6857">
                    <option value="" data-id="element-6858">Select Role</option>
                    <option value="Admin" data-id="element-6859">Admin</option>
                    <option value="Manager" data-id="element-6860">Manager</option>
                    <option value="Agent" data-id="element-6861">Agent</option>
                    <option value="Auditor" data-id="element-6862">Auditor</option>
                  </select>
                  <div className="ml-2" data-id="element-6863">
                    <HelpCircleIcon className="h-5 w-5 text-gray-400 cursor-help" title="Selecting a role will automatically set default permissions" data-id="element-6864" />
                  </div>
                </div>
              </div>
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4" data-id="element-6865">
                <div className="flex items-center mb-4" data-id="element-6866">
                  <ShieldIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 mr-2" data-id="element-6867" />
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-6868">
                    Permission Matrix
                  </h3>
                </div>
                <div className="overflow-x-auto" data-id="element-6869">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-6870">
                    <thead className="bg-gray-50 dark:bg-gray-800" data-id="element-6871">
                      <tr data-id="element-6872">
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-6873">
                          Module
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-6874">
                          View
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-6875">
                          Create
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-6876">
                          Edit
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-6877">
                          Delete
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-6878">
                          Export
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-6879">
                          Schedule
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-6880">
                      {Object.keys(user.permissions).map(module => <tr key={module} data-id="element-6881">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white" data-id="element-6882">
                            {module.charAt(0).toUpperCase() + module.slice(1)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-6883">
                            <input type="checkbox" checked={user.permissions[module as keyof typeof user.permissions].includes('view')} onChange={e => handlePermissionChange(module, 'view', e.target.checked)} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-6884" />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-6885">
                            {['dashboard', 'settings'].includes(module) ? <span className="text-gray-300 dark:text-gray-600" data-id="element-6886">
                                N/A
                              </span> : <input type="checkbox" checked={user.permissions[module as keyof typeof user.permissions].includes('create')} onChange={e => handlePermissionChange(module, 'create', e.target.checked)} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-6887" />}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-6888">
                            {['dashboard', 'reports'].includes(module) ? <span className="text-gray-300 dark:text-gray-600" data-id="element-6889">
                                N/A
                              </span> : <input type="checkbox" checked={user.permissions[module as keyof typeof user.permissions].includes('edit')} onChange={e => handlePermissionChange(module, 'edit', e.target.checked)} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-6890" />}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-6891">
                            {['dashboard', 'transactions', 'reports', 'settings'].includes(module) ? <span className="text-gray-300 dark:text-gray-600" data-id="element-6892">
                                N/A
                              </span> : <input type="checkbox" checked={user.permissions[module as keyof typeof user.permissions].includes('delete')} onChange={e => handlePermissionChange(module, 'delete', e.target.checked)} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-6893" />}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-6894">
                            {['dashboard', 'users', 'settings'].includes(module) ? <span className="text-gray-300 dark:text-gray-600" data-id="element-6895">
                                N/A
                              </span> : <input type="checkbox" checked={user.permissions[module as keyof typeof user.permissions].includes('export')} onChange={e => handlePermissionChange(module, 'export', e.target.checked)} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-6896" />}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-6897">
                            {module === 'reports' ? <input type="checkbox" checked={user.permissions.reports.includes('schedule')} onChange={e => handlePermissionChange('reports', 'schedule', e.target.checked)} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-6898" /> : <span className="text-gray-300 dark:text-gray-600" data-id="element-6899">
                                N/A
                              </span>}
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </Card>
        </div>
        {!isEditing && <div className="mt-6" data-id="element-6900">
            <Card title="Password" data-id="element-6901">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-6902">
                <div data-id="element-6903">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6904">
                    Password <span className="text-red-500" data-id="element-6905">*</span>
                  </label>
                  <input type="password" className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Enter password" required={!isEditing} data-id="element-6906" />
                </div>
                <div data-id="element-6907">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6908">
                    Confirm Password <span className="text-red-500" data-id="element-6909">*</span>
                  </label>
                  <input type="password" className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Confirm password" required={!isEditing} data-id="element-6910" />
                </div>
              </div>
              <div className="mt-4" data-id="element-6911">
                <div className="flex items-center" data-id="element-6912">
                  <input id="force-reset" name="force-reset" type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" defaultChecked data-id="element-6913" />
                  <label htmlFor="force-reset" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-6914">
                    Force user to change password on first login
                  </label>
                </div>
              </div>
            </Card>
          </div>}
      </form>
      {/* Form Actions */}
      <div className="flex justify-between pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-6915">
        <button type="button" className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" onClick={() => navigate('/users')} data-id="element-6916">
          Cancel
        </button>
        <button type="button" className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" onClick={handleSubmit} data-id="element-6917">
          <SaveIcon className="h-4 w-4 mr-2" data-id="element-6918" />
          {isEditing ? 'Update' : 'Create'} User
        </button>
      </div>
    </div>;
};
export default UserForm;